// Client-side planning for bulk knowledge uploads: split a picked set of
// files into what the server will accept and what gets skipped — with a
// reason, so the operator sees exactly which files never made it into the
// knowledge base instead of discovering the gap at retrieval time.
//
// The supported-extension list and size cap come from the backend
// (`GET …/ingest/supported-types`), never from a hardcoded copy here.

export type SkipReason = "unsupported" | "too-large" | "empty" | "junk";

export interface PickedFile {
  file: File;
  /** Path relative to the picked folder root; plain file name for flat picks. */
  relPath: string;
}

export interface SkippedFile {
  relPath: string;
  reason: SkipReason;
  sizeBytes: number;
}

export interface UploadPlan {
  accepted: PickedFile[];
  skipped: SkippedFile[];
}

export interface SupportedTypes {
  extensions: string[];
  maxFileSizeBytes: number;
}

/** OS metadata files that would otherwise clutter the skipped list as "unsupported". */
const JUNK_NAMES = new Set(["thumbs.db", "desktop.ini"]);

function isJunk(name: string): boolean {
  const n = name.toLowerCase();
  return n.startsWith(".") || JUNK_NAMES.has(n);
}

function hasSupportedExtension(name: string, extensions: string[]): boolean {
  const n = name.toLowerCase();
  return extensions.some((ext) => n.endsWith(ext.toLowerCase()));
}

export function planUploads(files: PickedFile[], supported: SupportedTypes): UploadPlan {
  const accepted: PickedFile[] = [];
  const skipped: SkippedFile[] = [];

  for (const picked of files) {
    const name = picked.file.name;
    const size = picked.file.size;
    const skip = (reason: SkipReason) =>
      skipped.push({ relPath: picked.relPath, reason, sizeBytes: size });

    if (isJunk(name)) skip("junk");
    else if (size === 0) skip("empty");
    else if (!hasSupportedExtension(name, supported.extensions)) skip("unsupported");
    else if (size > supported.maxFileSizeBytes) skip("too-large");
    else accepted.push(picked);
  }

  return { accepted, skipped };
}

/**
 * Normalize an `<input type="file">` selection. Folder picks
 * (`webkitdirectory`) carry the in-folder path on each file; flat picks
 * don't, so the name is the path.
 */
export function fromFileList(files: Iterable<File>): PickedFile[] {
  const out: PickedFile[] = [];
  for (const file of files) {
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
    out.push({ file, relPath: rel && rel.length > 0 ? rel : file.name });
  }
  return out;
}
